import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  REJECT_REASONS,
  type ModerationAction,
  useModerateAssetsMutation,
} from '@/client/api/assets/moderation';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const COPY: Record<ModerationAction, { title: string; body: string; cta: string; danger?: boolean }> = {
  hide: { title: 'Hide', body: 'Taken off the listing (the next photo becomes the cover). The seller is told why and cannot put it back.', cta: 'Hide', danger: true },
  reject: { title: 'Reject', body: 'Removed from the listing for breaking the rules. The seller is told why and cannot put it back.', cta: 'Reject', danger: true },
  restore: { title: 'Restore', body: 'Put back on the listing. The seller is told it was restored.', cta: 'Restore' },
  approve: { title: 'Approve', body: 'Marked as reviewed. It leaves the queue until it is analysed again.', cta: 'Approve' },
  flag: { title: 'Flag', body: 'Puts it in the review queue for a colleague. Internal: the seller is not told.', cta: 'Flag' },
  unflag: { title: 'Clear flag', body: 'Removes the internal flag.', cta: 'Clear flag' },
};

export interface ModerationDialogProps {
  action: ModerationAction | null;
  asids: number[];
  suggestedReason?: string | null;
  reasons?: Record<string, string>;
  onClose: () => void;
  onDone?: () => void;
}

/** One confirm for every moderation action, single or bulk. Hide and reject
 *  need a seller-facing reason; flag needs an internal note. */
const ModerationDialog = ({ action, asids, suggestedReason, reasons = REJECT_REASONS, onClose, onDone }: ModerationDialogProps) => {
  const moderate = useModerateAssetsMutation();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    setReason(suggestedReason ?? '');
    setNote('');
  }, [action, suggestedReason]);

  if (!action) return null;
  const copy = COPY[action];
  const needsReason = action === 'hide' || action === 'reject';
  const needsNote = action === 'flag' || reason === 'other';
  const ready = (!needsReason || reason) && (!needsNote || note.trim()) && asids.length > 0;
  const n = asids.length;

  const submit = async () => {
    try {
      const out = await moderate.mutateAsync({ asids, action, reason_code: needsReason ? reason : null, note: note.trim() || null });
      const unsent = out.notices.filter((x) => !x.sent).length;
      if (out.failed) toast.error(`${copy.title}: ${out.ok} done, ${out.failed} failed`);
      else toast.success(`${copy.title}: ${out.ok} photo${out.ok === 1 ? '' : 's'}${out.notices.length ? `, ${out.notices.length - unsent} seller notice${out.notices.length - unsent === 1 ? '' : 's'} sent` : ''}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not apply the decision'));
    }
  };

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title} {n === 1 ? `photo #${asids[0]}` : `${n} photos`}?</AlertDialogTitle>
          <AlertDialogDescription>{copy.body}</AlertDialogDescription>
        </AlertDialogHeader>
        {needsReason && (
          <select
            aria-label="Reason"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            <option value="">Reason the seller will see…</option>
            {Object.entries(reasons).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        )}
        <Input
          placeholder={action === 'flag' ? 'What should a colleague look at? (required)' : needsNote ? 'Note for the seller (required)' : 'Note (optional)'}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ready && void submit()}
        />
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={copy.danger ? 'bg-red-600 hover:bg-red-700' : undefined}
            disabled={!ready || moderate.isPending}
            onClick={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            {copy.cta}{n > 1 ? ` ${n}` : ''}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ModerationDialog;
